package com.universaliun.auth.web

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlin.js.Promise
import kotlin.js.json

/** Minimal typed-array binding — avoids relying on the stdlib's `org.khronos.webgl.Uint8Array`
 * overload set, which doesn't line up cleanly for this use case. */
@JsName("Uint8Array")
private external class JsBytes {
    constructor(length: Int)
    constructor(buffer: dynamic)

    val length: Int
}

private operator fun JsBytes.get(index: Int): Int = asDynamic()[index] as Int
private operator fun JsBytes.set(index: Int, value: Int) {
    asDynamic()[index] = value
}

/**
 * Bridges the browser's WebAuthn Credential Management API (not covered by Kotlin/JS's stdlib DOM
 * bindings, so handled via `dynamic`) to the JSON shapes the backend's
 * `PublicKeyCredential.parse{Registration,Assertion}ResponseJson` expects. Generic -- no
 * assumptions about which host app or backend DTOs are involved beyond that shared JSON contract,
 * which every AuthKit-backed host's WebAuthn routes use identically.
 *
 * The backend hands us `optionsJson` as `{"publicKey": {...}}` with byte fields (challenge,
 * user.id, credential ids) base64url-encoded as strings — the browser API needs those as
 * `ArrayBuffer`-backed typed arrays instead, and expects the reverse conversion on the way back.
 */

private fun base64UrlToBytes(b64url: String): JsBytes {
    val padded = b64url.replace('-', '+').replace('_', '/')
        .let { it + "=".repeat((4 - it.length % 4) % 4) }
    val binary = window.atob(padded)
    val bytes = JsBytes(binary.length)
    for (i in binary.indices) {
        bytes[i] = binary[i].code
    }
    return bytes
}

private fun bytesToBase64Url(buffer: dynamic): String {
    val bytes = JsBytes(buffer)
    val sb = StringBuilder()
    for (i in 0 until bytes.length) {
        sb.append((bytes[i] and 0xFF).toChar())
    }
    return window.btoa(sb.toString()).replace('+', '-').replace('/', '_').trimEnd('=')
}

/** Drives `navigator.credentials.create()` and returns the response JSON for a passkey
 *  registration `.../register/finish` call. */
suspend fun createPasskeyCredential(optionsJson: String): String {
    val options = JSON.parse<dynamic>(optionsJson)
    val publicKey = options.publicKey

    publicKey.challenge = base64UrlToBytes(publicKey.challenge as String)
    publicKey.user.id = base64UrlToBytes(publicKey.user.id as String)

    val excludeCredentials = publicKey.excludeCredentials
    if (excludeCredentials != undefined) {
        val length = excludeCredentials.length as Int
        for (i in 0 until length) {
            excludeCredentials[i].id = base64UrlToBytes(excludeCredentials[i].id as String)
        }
    }

    val credential = (window.navigator.asDynamic().credentials.create(options) as Promise<dynamic>).await()

    val result = json(
        "id" to credential.id,
        "rawId" to bytesToBase64Url(credential.rawId),
        "type" to credential.type,
        "response" to json(
            "attestationObject" to bytesToBase64Url(credential.response.attestationObject),
            "clientDataJSON" to bytesToBase64Url(credential.response.clientDataJSON),
        ),
        "clientExtensionResults" to json(),
    )
    return JSON.stringify(result)
}

/** Drives `navigator.credentials.get()` and returns the response JSON for a passkey login
 *  `.../login/finish` call. */
suspend fun getPasskeyAssertion(optionsJson: String): String {
    val options = JSON.parse<dynamic>(optionsJson)
    val publicKey = options.publicKey

    publicKey.challenge = base64UrlToBytes(publicKey.challenge as String)

    val allowCredentials = publicKey.allowCredentials
    if (allowCredentials != undefined) {
        val length = allowCredentials.length as Int
        for (i in 0 until length) {
            allowCredentials[i].id = base64UrlToBytes(allowCredentials[i].id as String)
        }
    }

    val credential = (window.navigator.asDynamic().credentials.get(options) as Promise<dynamic>).await()
    val userHandle = credential.response.userHandle

    val result = json(
        "id" to credential.id,
        "rawId" to bytesToBase64Url(credential.rawId),
        "type" to credential.type,
        "response" to json(
            "authenticatorData" to bytesToBase64Url(credential.response.authenticatorData),
            "clientDataJSON" to bytesToBase64Url(credential.response.clientDataJSON),
            "signature" to bytesToBase64Url(credential.response.signature),
            "userHandle" to (if (userHandle != undefined && userHandle != null) bytesToBase64Url(userHandle) else null),
        ),
        "clientExtensionResults" to json(),
    )
    return JSON.stringify(result)
}

/** True when the browser exposes the WebAuthn Credential Management API at all. */
fun isPasskeySupported(): Boolean =
    window.navigator.asDynamic().credentials != undefined
